'use client';

import { FC, useMemo, useState } from 'react';

import { useAuthentication, getUserType } from '@ravago/shared/authentication';
import { UserPermissions } from '@ravago/shared/page-data/models/config';
import { CallOffOrderColor, DynamicProductBlockColor, ProductDetail, QuotedColor } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';

import { useSearchParams } from 'next/navigation';

import { useCallOffAgreementContext } from '../../../../context/call-off-agreement-context';
import { useFlyoutContext } from '../../../../hooks/use-flyout-context';
import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { DynamicProductBlockCallOffFields } from '../../../shared/dynamic-product-block/dynamic-product-block-call-off-fields';
import { DynamicProductBlockQuotedColorFields } from '../../../shared/dynamic-product-block/dynamic-product-block-quoted-color-fields';
import { ColorPickerValue, ExtendedColorPicker } from '../../../shared/extended-color-picker/extended-color-picker';

import { QuoteFlyout } from './flyout/quote/quote-flyout';
import { RequestForQuotationFlyout } from './flyout/request-for-quotation/request-for-quotation-flyout';
import { RequestForQuotationButton } from './request-for-quotation-button';
import { RequestForQuotationContentGuard } from './request-for-quotation-content-guard';
import { RequestForQuotationDesktop } from './request-for-quotation-desktop';
import { RequestForQuotationMobile } from './request-for-quotation-mobile';

const setOrRemoveParam = (params: URLSearchParams, key: string, value: string | undefined) => {
   if (value) {
      params.set(key, value);
   } else {
      params.delete(key);
   }
};

const appendSearchParams = (params: URLSearchParams, value: ColorPickerValue | undefined) => {
   setOrRemoveParam(params, 'color', value?.colorName);
   setOrRemoveParam(params, 'colorCode', value?.producerColorCode);
   setOrRemoveParam(params, 'desiredColor', value?.desiredColor);
};

type Props = {
   productDetail: ProductDetail | undefined;
   callOffOrderColors: CallOffOrderColor[];
   quotedColors: QuotedColor[];
   defaultColor: DynamicProductBlockColor | undefined;
   userPermissions: UserPermissions;
   usesAgreementApp: boolean;
};

export const RequestForQuotation: FC<Props> = ({
   callOffOrderColors,
   quotedColors,
   productDetail,
   userPermissions,
   defaultColor,
   usesAgreementApp
}) => {
   const searchParams = useSearchParams();
   const { desktop } = useBreakpoint();
   const { user, accessToken } = useAuthentication();

   const [color, setColor] = useState<ColorPickerValue | undefined>(defaultColor);
   const [quantityAmount, setQuantityAmount] = useState<number | undefined>(defaultColor?.quantity?.amount);

   const [quoteFlyoutOpen, setQuoteFlyoutOpen] = useState(false);
   const [rfqFlyoutOpen, setRfqFlyoutOpen] = useState(searchParams.get('flyout') === 'open');

   const { dispatch: flyoutDispatch } = useFlyoutContext();
   const { state: callOffAgreementState, dispatch: callOffAgreementDispatch } = useCallOffAgreementContext();

   const selectedCallOffOrderColor = useMemo(
      () => callOffOrderColors?.find((callOffOrderColor) => callOffOrderColor.recommendedCallOff.id === color?.callOffOrderId),
      [callOffOrderColors, color]
   );

   const selectedQuotedColor = useMemo(
      () =>
         quotedColors?.find(
            (quotedColor) =>
               !!color &&
               color.isQuotedColor &&
               quotedColor.color.id === color.colorId &&
               quotedColor.color.producerColorCode === color.producerColorCode
         ),
      [quotedColors, color]
   );

   const setQueryParams = (value: ColorPickerValue | undefined) => {
      const params = new URLSearchParams(searchParams);
      appendSearchParams(params, value);
      window.history.replaceState({}, '', `?${params}`);
   };

   const handleColorChange = (value: ColorPickerValue | undefined) => {
      setColor(value);
      setQueryParams(value);
      setQuantityAmount(undefined);
   };

   const productBlockTitle = useMemo(() => {
      if (color?.isQuotedColor || color?.callOffOrderId) {
         return 'request-for-quotation.order-now';
      }
      return 'request-for-quotation.request-quote';
   }, [color]);

   const handleOpenRfqFlyout = () => {
      if (!productDetail) return;
      setRfqFlyoutOpen(true);
      TrackingUtils.track('rfq_click_start_quote_request', {
         productName: productDetail.description,
         productId: productDetail.id,
         userLoggedIn: JSON.stringify(!!user),
         userType: getUserType(user)
      });
   };

   const handleContinueCallOff = () => {
      if (!selectedCallOffOrderColor) return;

      if (callOffAgreementState.callOffId !== selectedCallOffOrderColor.recommendedCallOff.id) {
         callOffAgreementDispatch?.({ type: 'clear_call_off', value: {} });
      }
      callOffAgreementDispatch?.({
         type: 'set_call_off_id',
         value: { callOffId: selectedCallOffOrderColor.recommendedCallOff.id }
      });

      callOffAgreementDispatch?.({
         type: 'set_opened_from',
         value: { openedFrom: 'dynamic-product-block' }
      });

      callOffAgreementDispatch?.({
         type: 'set_call_off_quantity',
         value: {
            quantity: {
               amount: quantityAmount ?? 0,
               unitOfMeasure: selectedCallOffOrderColor.recommendedCallOff.unitPrice.unitOfMeasure
            }
         }
      });

      callOffAgreementDispatch?.({
         type: 'set_uses_agreement_app',
         value: {
            usesAgreementApp
         }
      });

      flyoutDispatch?.({
         type: 'open_flyout',
         value: { flyoutType: 'call-off-agreement' }
      });
   };

   const handleCommentClicked = () => {
      setQuoteFlyoutOpen(true);
   };

   const handleNewQuote = () => {
      if (selectedCallOffOrderColor) {
         handleColorChange({
            colorId: selectedCallOffOrderColor.color.id,
            colorName: selectedCallOffOrderColor.color.name,
            producerColorCode: selectedCallOffOrderColor.color.producerColorCode,
            desiredColor: undefined,
            callOffOrderId: undefined,
            isQuotedColor: false
         });
      } else if (selectedQuotedColor) {
         handleColorChange({
            colorId: selectedQuotedColor.color.id,
            colorName: selectedQuotedColor.color.name,
            producerColorCode: selectedQuotedColor.color.producerColorCode,
            desiredColor: undefined,
            callOffOrderId: undefined,
            isQuotedColor: false
         });
      }
   };

   const handleComplete = () => {
      const path = window.location.href.split('?')[0];
      window.history.pushState(null, '', path);
      setRfqFlyoutOpen(false);
   };

   const content = (
      <Box gap="lg" direction="column">
         <RequestForQuotationContentGuard productNotFound={!productDetail} userPermissions={userPermissions}>
            {productDetail?.colors && (
               <ExtendedColorPicker
                  id="dynamic-product-block-color-picker"
                  colors={productDetail.colors}
                  callOffOrderColors={callOffOrderColors}
                  quotedColors={quotedColors}
                  value={color}
                  onChange={handleColorChange}
               />
            )}
            {selectedCallOffOrderColor && accessToken && (
               <DynamicProductBlockCallOffFields
                  id="dynamic-product-block-call-off-fields"
                  quantityAmount={quantityAmount}
                  callOffOrderColor={selectedCallOffOrderColor}
                  onQuantityAmountChanged={setQuantityAmount}
                  usesAgreementApp={usesAgreementApp}
               />
            )}
            {selectedQuotedColor && accessToken && (
               <DynamicProductBlockQuotedColorFields quotedColor={selectedQuotedColor} token={accessToken} onCommentClicked={handleCommentClicked} />
            )}
         </RequestForQuotationContentGuard>
         <RequestForQuotationButton
            userPermissions={userPermissions}
            color={color}
            quantityAmount={quantityAmount}
            openFlyout={handleOpenRfqFlyout}
            newQuote={handleNewQuote}
            continueCallOff={handleContinueCallOff}
         />
      </Box>
   );

   return (
      <>
         <BreakpointSwitch includeViews={['desktop']}>
            <RequestForQuotationDesktop title={productBlockTitle}>{content}</RequestForQuotationDesktop>
         </BreakpointSwitch>
         {!desktop && <RequestForQuotationMobile title={productBlockTitle}>{content}</RequestForQuotationMobile>}

         {productDetail && color && selectedQuotedColor && (
            <QuoteFlyout
               product={productDetail}
               color={color}
               quotedColor={selectedQuotedColor}
               open={quoteFlyoutOpen}
               onFlyoutClose={() => setQuoteFlyoutOpen(false)}
            />
         )}

         {productDetail && color && (
            <RequestForQuotationFlyout
               userPermissions={userPermissions}
               open={rfqFlyoutOpen}
               productId={productDetail.id}
               productDescription={productDetail.description}
               isCustomerProduct={false}
               defaultProduct={productDetail}
               color={color}
               type="QUOTATION"
               from="PRODUCT_DETAILS"
               onClose={() => setRfqFlyoutOpen(false)}
               onComplete={handleComplete}
            />
         )}
      </>
   );
};
